import { useState, useEffect, useRef } from 'react';

/**
 * Custom hook for countdown timer
 * @param {number} initialSeconds - Initial countdown value in seconds
 * @returns {object} { seconds, isActive, reset, pause, resume }
 */
const useCountdown = (initialSeconds = 0) => {
  const [seconds, setSeconds] = useState(initialSeconds);
  const [isActive, setIsActive] = useState(true);
  const intervalRef = useRef(null);

  useEffect(() => {
    if (seconds > 0 && isActive) {
      intervalRef.current = setTimeout(() => {
        setSeconds(s => s - 1);
      }, 1000);

      return () => {
        if (intervalRef.current) {
          clearTimeout(intervalRef.current);
        }
      };
    } else if (seconds === 0) {
      setIsActive(false);
    }
  }, [seconds, isActive]);

  const reset = (newSeconds) => {
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
    setSeconds(newSeconds !== undefined ? newSeconds : initialSeconds);
    setIsActive(true);
  };

  const pause = () => {
    setIsActive(false);
    if (intervalRef.current) {
      clearTimeout(intervalRef.current);
    }
  };

  const resume = () => {
    if (seconds > 0) {
      setIsActive(true);
    }
  };

  return {
    seconds,
    isActive: seconds > 0 && isActive,
    reset,
    pause,
    resume
  };
};

export default useCountdown;
